import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MOUNTAINS_DATA } from '../data/mountains';
import { getOrganizationsByMountain } from '../data/organizations';
import { getCountries, getOrganizationsByCountryAndLevel, INFLUENCE_LEVELS } from '../data/countries';
import { ArrowLeft, Users, Target, Zap, ExternalLink, Globe, MapPin } from 'lucide-react';

function MountainDetail() {
  const { mountainKey } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCountry, setSelectedCountry] = useState(getCountries()[0]);
  const [selectedLevel, setSelectedLevel] = useState('national');

  const mountain = MOUNTAINS_DATA[mountainKey];

  if (!mountain) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold mb-4">Mountain Not Found</h1>
        <Link to="/" className="text-orange-500 hover:text-orange-400 flex items-center gap-2">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header with Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={mountain.image}
          alt={mountain.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 p-2 bg-zinc-900/80 hover:bg-orange-600 rounded-lg transition flex items-center gap-2 z-10"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        {/* Title Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <h1 className="text-6xl font-black uppercase mb-4" style={{ color: mountain.color }}>
            {mountainKey}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl px-6">
            {mountain.desc}
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-zinc-800 sticky top-0 bg-black/95 backdrop-blur">
        <div className="container mx-auto max-w-6xl">
          <div className="flex gap-8 px-6 overflow-x-auto">
            {['overview', 'organizations', 'leaders', 'initiatives', 'join'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-4 font-semibold capitalize transition whitespace-nowrap ${
                  activeTab === tab
                    ? `border-b-2 text-white`
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                style={{
                  borderColor: activeTab === tab ? mountain.color : 'transparent',
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-6xl px-6 py-16">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-12">
            <div>
              <h2 className="text-4xl font-bold mb-6">About This Mountain</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Target size={28} style={{ color: mountain.color }} />
                    Mission
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {mountain.desc}
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Zap size={28} style={{ color: mountain.color }} />
                    Impact
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    Leaders in this mountain shape culture, influence decision-making, and drive transformation across society. By developing kingdom-minded leaders here, we catalyze lasting change.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/50 rounded-lg p-8 border border-zinc-800">
              <h3 className="text-2xl font-bold mb-4">Key Areas of Focus</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Strategic Leadership',
                  'Cultural Transformation',
                  'Network Development',
                  'Resource Mobilization',
                  'Vision Implementation',
                  'Impact Measurement',
                ].map((area, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: mountain.color }}
                    ></div>
                    <span className="text-gray-200">{area}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <div>
            <h2 className="text-4xl font-bold mb-2">Global & Local Influence</h2>
            <p className="text-gray-400 mb-8">
              Organizations shaping this mountain across the globe and within specific countries at different governance levels
            </p>

            {/* Global Section */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Globe size={28} style={{ color: mountain.color }} />
                Top Global Organizations
              </h3>
              <p className="text-gray-400 mb-6">
                Influential organizations and institutions shaping this mountain worldwide
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getOrganizationsByMountain(mountainKey).map((org, i) => (
                  <a
                    key={i}
                    href={org.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-zinc-900/50 rounded-lg p-5 border border-zinc-800 hover:border-orange-500/50 hover:bg-zinc-800/50 transition group cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg group-hover:text-orange-500 transition">
                          {org.name}
                        </h4>
                        <p className="text-sm text-gray-400 mt-1">{org.category}</p>
                      </div>
                      <ExternalLink size={18} className="text-gray-600 group-hover:text-orange-500 transition flex-shrink-0 ml-2" />
                    </div>
                    <p className="text-sm text-gray-300">
                      {org.influence}
                    </p>
                  </a>
                ))}
              </div>
            </div>

            {/* Country & Level Section */}
            <div className="border-t border-zinc-800 pt-12">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <MapPin size={28} style={{ color: mountain.color }} />
                Organizations by Country & Government Level
              </h3>

              {/* Filters */}
              <div className="bg-zinc-900/50 rounded-lg p-6 border border-zinc-800 mb-8">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Country Selector */}
                  <div>
                    <label className="block text-sm font-bold text-gray-300 mb-3">Select Country</label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white hover:border-orange-500/50 focus:outline-none focus:border-orange-500"
                    >
                      {getCountries().map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Level Selector */}
                  <div>
                    <label className="block text-sm font-bold text-gray-300 mb-3">Influence Level</label>
                    <div className="flex gap-2">
                      {INFLUENCE_LEVELS.map((level) => (
                        <button
                          key={level}
                          onClick={() => setSelectedLevel(level)}
                          className={`flex-1 py-2 px-3 rounded-lg font-semibold capitalize transition ${
                            selectedLevel === level
                              ? 'bg-orange-600 text-white'
                              : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                          }`}
                        >
                          {level === 'community' ? 'Community' : level === 'state' ? 'Regional' : 'National'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Organizations List */}
              <div>
                <h4 className="text-xl font-bold mb-4 capitalize">
                  {selectedCountry} - {selectedLevel === 'community' ? 'Community' : selectedLevel === 'state' ? 'Regional' : 'National'} Level Organizations
                </h4>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getOrganizationsByCountryAndLevel(selectedCountry, selectedLevel, mountainKey).length > 0 ? (
                    getOrganizationsByCountryAndLevel(selectedCountry, selectedLevel, mountainKey).map((org, i) => (
                      <div
                        key={i}
                        className="bg-zinc-900/50 rounded-lg p-5 border border-zinc-800 hover:border-orange-500/50 hover:bg-zinc-800/50 transition"
                      >
                        <h5 className="font-bold text-lg mb-2 text-white">
                          {org.name}
                        </h5>
                        <p className="text-sm text-gray-400 mb-3">{org.category}</p>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: mountain.color }}
                          ></span>
                          <span className="text-xs text-gray-400 capitalize">{mountainKey}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-gray-400">
                      <p>No organizations found for this mountain in {selectedCountry} at the {selectedLevel} level.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaders Tab */}
        {activeTab === 'leaders' && (
          <div>
            <h2 className="text-4xl font-bold mb-6">Leaders in This Mountain</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900/50 rounded-lg overflow-hidden border border-zinc-800 hover:border-orange-500/50 transition">
                  <div className="h-48 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    <Users size={48} className="text-zinc-700" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-2">Leader Name</h3>
                    <p className="text-gray-400 text-sm mb-4">Position and role</p>
                    <button className="w-full bg-zinc-800 hover:bg-orange-600 py-2 rounded transition text-sm font-semibold">
                      View Profile
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Initiatives Tab */}
        {activeTab === 'initiatives' && (
          <div>
            <h2 className="text-4xl font-bold mb-6">Active Initiatives</h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Leadership Development Program',
                  desc: 'Intensive training for emerging leaders',
                },
                {
                  title: 'Network Expansion',
                  desc: 'Building connections across sectors',
                },
                {
                  title: 'Research & Strategy',
                  desc: 'Understanding cultural dynamics and opportunities',
                },
              ].map((initiative, i) => (
                <div key={i} className="bg-zinc-900/50 p-6 rounded-lg border border-zinc-800 hover:border-orange-500/50 transition cursor-pointer">
                  <h3 className="text-xl font-bold mb-2">{initiative.title}</h3>
                  <p className="text-gray-400">{initiative.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join Tab */}
        {activeTab === 'join' && (
          <div className="max-w-2xl">
            <h2 className="text-4xl font-bold mb-6">Get Involved</h2>
            <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 rounded-lg p-8 border border-orange-500/30">
              <h3 className="text-2xl font-bold mb-4">Join This Mountain</h3>
              <p className="text-gray-300 mb-6">
                Are you a leader or influencer in this sphere? Join our network and connect with like-minded kingdom-minded leaders transforming this mountain.
              </p>
              <div className="space-y-3">
                <button
                  style={{ backgroundColor: mountain.color }}
                  className="w-full py-3 rounded-lg font-bold text-black hover:opacity-90 transition"
                >
                  Express Interest
                </button>
                <Link
                  to="/programs"
                  className="block w-full text-center py-3 rounded-lg font-bold border border-orange-500 text-orange-500 hover:bg-orange-500/10 transition"
                >
                  Explore Programs
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Other Mountains Navigation */}
      <div className="border-t border-zinc-800 py-16 px-6 bg-zinc-950/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold mb-8">Other Mountains</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(MOUNTAINS_DATA)
              .filter(([key]) => key !== mountainKey)
              .map(([key, m]) => (
                <Link
                  key={key}
                  to={`/mountain/${key}`}
                  className="group relative h-48 rounded-lg overflow-hidden cursor-pointer border border-zinc-800 hover:border-orange-500/50 transition"
                >
                  <img
                    src={m.image}
                    className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-110"
                    alt={key}
                  />
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-center p-4 transition group-hover:bg-black/80">
                    <div>
                      <h3 className="text-xl font-bold text-white uppercase">{key}</h3>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MountainDetail;
